import { useEffect, useMemo, useState } from 'react';
import {useNavigate } from 'react-router-dom';
import { signOutRedirect } from '../../auth/cognitoLogout';
import { useApiWithAuth } from '../../auth/apiHelper';
import {FaPlus, FaSignOutAlt, FaBriefcase, FaEdit, FaTrash } from 'react-icons/fa';
import styles from './adminJobs.module.scss';

type JobFormData = {
    name: string;
    location: string;
    remoteAllowed: boolean;
    jobRoles: string;
    description: string;
    active: boolean;
}

type Job = JobFormData & {
    jobId: string;
    postedDate: string; 
};

const AdminJobsPage = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [jobs, setJobs] = useState<Job[]>([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const fetchWithAuth = useApiWithAuth();

    // CRA env var
    const apiUrl = useMemo(() => {
        const raw = process.env.REACT_APP_JOBLISTINGS_API || '';
        return raw.trim().replace(/\/$/, '');
    }, []);

    const [formData, setFormData] = useState<JobFormData>({
        name: '',
        location: '',
        remoteAllowed: true,
        jobRoles: '',
        description: '',
        active: true
    });

    // Load all jobs on mount
    useEffect(() => {
        loadJobs();
    }, []);

    const loadJobs = async () => {
        try {
            setJobsLoading(true);

            if (!apiUrl) {
                console.error('REACT_APP_JOBLISTINGS_API is empty');
                setErrorMessage('Jobs API URL is not configured.');
                return;
            }

            const url = `${apiUrl}/jobs`;
            console.log('Loading jobs from:', url);

            const response = await fetch(url);
            const text = await response.text();
            console.log('Jobs API status:', response.status, 'body:', text);

            if (!response.ok) throw new Error(`Failed to load jobs (${response.status})`);

            const parsed = text ? JSON.parse(text) : [];
            const items = Array.isArray(parsed) ? parsed : (parsed.items ?? []);
            // const data = await response.json();
            setJobs(items);
        } catch (error) {
            console.error('Error loading jobs:', error);
            setErrorMessage('Failed to load jobs');
            setJobs([]);
        } finally {
            setJobsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const handleBooleanToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked}));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const payload = {
                ...formData,
                jobId: editingId || `job-${Date.now()}`,
            };

            const response = await fetchWithAuth(`${apiUrl}/jobs`, {
                method: `POST`,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            // start of debug code
            const raw = await response.text();
            console.log('POST /jobs status:', response.status, 'body:', raw);
            // end of debug code

            if (!response.ok) {
                let msg = `Failed to create job (${response.status})`;
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed?.message) msg = parsed.message;
                } catch {}
                throw new Error(msg);
            }

            setSuccessMessage(`Job listing ${editingId ? 'updated' : 'created'} successfully!`);

            // reset form
            setFormData({
                name: '',
                location: '',
                remoteAllowed: true,
                jobRoles: '',
                description: '',
                active: true
            });

            setEditingId(null);
            loadJobs();

            setTimeout(() => setSuccessMessage(''), 5000);   
        } catch (error: any) {
            console.error('Error creating job listing:', error);
            setErrorMessage(error.message || 'Failed to create job listing');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditEvent = (job: Job) => {
        setFormData({
            name: job.name,
            location: job.location,
            remoteAllowed: job.remoteAllowed,
            jobRoles: job.jobRoles,
            description: job.description,
            active: job.active
        });
        setEditingId(job.jobId);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({
            name: '',
            location: '',
            remoteAllowed: true,
            jobRoles: '',
            description: '',
            active: true
        });
    };

    const handleDeleteJob = async (job: Job) => {
        const confirmed = window.confirm(`Delete "${job.name}"? This cannot be undone.`);
        if (!confirmed) return;

        setErrorMessage('');
        setSuccessMessage('');
        setDeletingId(job.jobId);

        try {
            const qs = new URLSearchParams({
                jobRoles: job.jobRoles,
                jobId: job.jobId,
            }).toString();

            const response = await fetchWithAuth(`${apiUrl}/jobs?${qs}`, {
                method: 'DELETE',
            });

            const raw = await response.text();

            if (!response.ok) {
                let msg = `Failed to delete job (${response.status})`;
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed?.message) msg = parsed.message;
                } catch {}
                throw new Error(msg);
            }

            setSuccessMessage('Job listing deleted successfully!');
            await loadJobs();
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error: any) {
            console.error('Error deleting job listing:', error);
            setErrorMessage(error.message || 'Failed to delete job listing');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className={styles.adminPage}>

            <div className={styles.adminContent}>
                <div className={styles.container}>
                    <div className={styles.adminHeader}>
                        <h1><FaBriefcase /> Job Listing Management</h1>
                        <button onClick={signOutRedirect} className={styles.logoutBtn}>
                            <FaSignOutAlt /> Logout
                        </button>
                    </div>

                    {successMessage && (
                        <div className={styles.successAlert}>{successMessage}</div>
                    )}

                    {errorMessage && (
                        <div className={styles.errorAlert}>{errorMessage}</div>
                    )}

                    <div className={styles.formCard}>
                        <h2><FaPlus /> {editingId ? 'Edit Job Listing' : 'Create New Job Listing'}</h2>

                        <form onSubmit={handleSubmit}>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="name">Job Title *</label>
                                    <input
                                        id="name"
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g. ServiceNow Developer"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="location">Location *</label>
                                    <input
                                        id="location"
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g. Charleston, WV"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="remoteAllowed" className={styles.checkboxLabel}>
                                        <input
                                            id="remoteAllowed"
                                            type="checkbox"
                                            name="remoteAllowed"
                                            checked={formData.remoteAllowed}
                                            onChange={handleBooleanToggle}
                                        />
                                        <span>Remote Work Allowed?</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="jobRoles">String ID for Job Role *</label>
                                    <input
                                        id="jobRoles"
                                        type="text"
                                        name="jobRoles"
                                        value={formData.jobRoles}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g. SN_Developer"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="description">Job Description *</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={4}
                                        placeholder="Provide a brief description of the job role and responsibilities"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="active" className={styles.checkboxLabel}>
                                        <input
                                            id="active"
                                            type="checkbox"
                                            name="active"
                                            checked={formData.active}
                                            onChange={handleBooleanToggle}
                                        />
                                        <span>Active (Display on public Careers page)</span>
                                    </label>
                                </div>

                                <div className={styles.formActions}>
                                    <button
                                        type="submit"
                                        className={styles.submitBtn}
                                        disabled={isSubmitting}
                                    >
                                        {editingId ? <><FaEdit /> Update Job</> : <><FaPlus /> Create Job</>}
                                    </button>
                                    {editingId && (
                                        <button
                                            type="button"
                                            className={styles.cancelBtn}
                                            onClick={handleCancelEdit}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Job Listings Table */}
                    <div className={styles.jobsTableCard}>
                        <h2>All Job Listings</h2>
                        {jobsLoading && <p>Loading job listings...</p>}
                        {jobs.length == 0 && !jobsLoading && <p>No job listings found.</p>}

                        <div className={styles.jobsTable}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Job Title</th>
                                        <th>Location</th>
                                        <th>Active</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {jobs.map(job => (
                                        <tr key={job.jobId}>
                                            <td className={styles.jobTitle}>{job.name}</td>
                                            <td>{job.location}</td>
                                            <td>
                                                <span className={`${styles.activeBadge} ${job.active ? styles.active : styles.inactive}`}>
                                                    {job.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => handleEditEvent(job)}
                                                    title="Edit Job Listing"
                                                >
                                                    <FaEdit /> Edit
                                                </button>

                                                <button
                                                    className={styles.deleteBtn}
                                                    onClick={() => handleDeleteJob(job)}
                                                    title="Delete Job Listing"
                                                    disabled={deletingId === job.jobId}
                                                >
                                                    <FaTrash /> {deletingId === job.jobId ? 'Deleting...' : 'Delete'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>    
                    </div>       
                </div>
            </div>
        </div>
    );
};

export default AdminJobsPage;